import { concat, find } from 'lodash';
import type { IDashboardItem } from '../shared/interfaces/dashboard-item.interface';
import type { INavigationItem } from '../shared/interfaces/navigation-item.interface';
import type { AccountInfo } from '@azure/msal-browser';
import { PermissionService } from '../modules/auth/services/permission.service';
import { AuthService } from '@/modules/auth/services/auth.service';

const authServices = new PermissionService();
const authService = new AuthService();
const ADMIN_ROLE = import.meta.env.VITE_MSAL_ADMIN_ROLE_NAME;
const READER_ROLE = import.meta.env.VITE_MSAL_READER_ROLE_NAME;

export type AddNewItemType = 'model' | 'category';

export interface IAddNewItemConfig {
    match: string[];
    actionLabel: string;
    dialogTitle: string;
    nameFieldLabel: string;
    type: AddNewItemType;
    serviceKey: 'machineModel' | 'implementCategory';
    reloadAction: 'loadMachineModels' | 'loadImplementFilters';
    template: {
        name: string;
        description: string;
        class_id?: string;
    };
}

export const APP_ADD_NEW_ITEM_CONFIG: IAddNewItemConfig[] = [
    {
        match: ['core-model', 'core-models', 'model'],
        actionLabel: 'add_new_model',
        dialogTitle: 'add_new_model',
        nameFieldLabel: 'label_model_name',
        type: 'model',
        serviceKey: 'machineModel',
        reloadAction: 'loadMachineModels',
        template: {
            name: '',
            description: '',
        },
    },
    {
        match: ['core-category', 'core-categories', 'category'],
        actionLabel: 'add_new_category',
        dialogTitle: 'add_new_category',
        nameFieldLabel: 'label_category_name',
        type: 'category',
        serviceKey: 'implementCategory',
        reloadAction: 'loadImplementFilters',
        template: {
            name: '',
            description: '',
            class_id: '',
        },
    },
];

export const APP_MACHINES_CORE_STEPS: string[] = ['core', 'Core'];
export const APP_CORE_STEP = 'core';

const isAdmin = (): boolean => authService.hasPermissionByRoles([ADMIN_ROLE]);
const isReader = (): boolean => authService.hasPermissionByRoles([READER_ROLE]);

const getManageText = (): string => {
    if (isAdmin()) return 'navigation_text_manage';
    if (isReader()) return 'navigation_text_view';
    return 'navigation_text_view';
};

const getMachinesText = (): string => {
    if (isAdmin()) return 'navigation_text_manage_machines';
    return 'navigation_text_view_machines';
};

const getImplementsText = (): string => {
    if (isAdmin()) return 'navigation_text_manage_implements';
    return 'navigation_text_view_implements';
};

const getManageIcon = (): string => {
    return isAdmin() ? 'edit' : 'visibility';
};

export const APP_NAVIGATION: INavigationItem[] = [
    {
        get text(): string {
            return getManageText();
        },
        link: 'Manage',
        roles: concat(
            authServices.getPermissionRoles('machines.read'),
            authServices.getPermissionRoles('implements.read')
        ),
        subItems: [],
    },
    {
        text: 'navigation_text_create',
        link: 'Manage',
        roles: concat(
            authServices.getPermissionRoles('machines.create'),
            authServices.getPermissionRoles('implements.create')
        ),
        subItems: [
            {
                text: 'navigation_text_create_machines',
                link: 'CreateMachine',
                icon: 'agriculture',
                subItems: [],
                roles: authServices.getPermissionRoles('machines.create'),
            },
            {
                text: 'navigation_text_create_implements',
                link: 'CreateImplement',
                icon: 'build_circle',
                subItems: [],
                roles: authServices.getPermissionRoles('implements.create'),
            },
        ],
    },
    {
        text: 'navigation_text_compatibility',
        link: 'Compatibility',
        subItems: [],
        roles: authServices.getPermissionRoles('compatibility.read'),
    },
    {
        text: 'navigation_text_bulk_update',
        link: 'BulkUpdate',
        subItems: [],
        roles: authServices.getPermissionRoles('bulk_update.read'),
    },
];

export const APP_DASHBOARD_NAVIGATION: INavigationItem[] = [
    {
        text: 'navigation_text_machines',
        link: 'CreateMachine',
        subItems: [
            {
                text: 'navigation_text_create_machines',
                link: 'CreateMachine',
                icon: 'add',
                subItems: [],
                roles: authServices.getPermissionRoles('machines.create'),
            },
            {
                get text(): string {
                    return getMachinesText();
                },
                link: 'MachinesHome',
                get icon(): string {
                    return getManageIcon();
                },
                subItems: [],
                roles: authServices.getPermissionRoles('machines.read'),
            },
        ],
        roles: authServices.getPermissionRoles('machines.read'),
    },
    {
        text: 'navigation_text_implements',
        link: 'CreateImplement',
        subItems: [
            {
                text: 'navigation_text_create_implements',
                link: 'CreateImplement',
                icon: 'add',
                subItems: [],
                roles: authServices.getPermissionRoles('implements.create'),
            },
            {
                get text(): string {
                    return getImplementsText();
                },
                subItems: [],
                link: 'ImplementsHome',
                get icon(): string {
                    return getManageIcon();
                },
                roles: authServices.getPermissionRoles('implements.read'),
            },
        ],
        roles: authServices.getPermissionRoles('implements.read'),
    },
    {
        subItems: [],
        link: 'Compatibility',
        roles: authServices.getPermissionRoles('compatibility.read'),
        text: 'navigation_text_compatibility',
    },
    {
        link: 'BulkUpdate',
        text: 'navigation_text_bulk_update',
        roles: authServices.getPermissionRoles('bulk_update.read'),
        subItems: [],
    },
];

export const APP_DASHBOARD_ITEMS: IDashboardItem[] = [
    {
        background: 'src/assets/dashboard_machines_background.png',
        text: 'dashboard_text_machines',
        get actions(): INavigationItem[] {
            return find(APP_DASHBOARD_NAVIGATION, (x) => x.text === 'navigation_text_machines')?.subItems ?? [];
        },
    },
    {
        text: 'dashboard_text_implements',
        background: 'src/assets/dashboard_implements_background.png',
        get actions(): INavigationItem[] {
            return find(APP_DASHBOARD_NAVIGATION, (x) => x.text === 'navigation_text_implements')?.subItems ?? [];
        },
    },
];

export const APP_USER_MENU: INavigationItem[] = [
    // {
    //     text: 'navigation_auth_profile',
    //     link: 'Profile',
    //     subItems: [],
    // },
    {
        text: 'navigation_auth_logout',
        link: 'MSALLogout',
        subItems: [],
        roles: [],
    },
];

export const APP_COMPATIBILITY_ITEMS: INavigationItem[] = [
    {
        text: 'APPROVED',
        link: 'APPROVED',
        subItems: [],
        roles: [],
    },
    {
        text: 'NOT_APPROVED',
        link: 'NOT_APPROVED',
        subItems: [],
        roles: [],
    },
    {
        text: 'MAYBE_APPROVED',
        link: 'MAYBE_APPROVED',
        subItems: [],
        roles: [],
    },
];

export const APP_SEARCH_TYPES: INavigationItem[] = [
    {
        text: 'search_types_label_machines',
        link: 'machines',
        icon: 'agriculture',
        subItems: [],
        roles: authServices.getPermissionRoles('machines.read'),
    },
    {
        text: 'search_types_label_implements',
        link: 'implements',
        icon: 'build_circle',
        subItems: [],
        roles: authServices.getPermissionRoles('implements.read'),
    },
];

export const APP_MACHINE_ACTIONS: INavigationItem[] = [
    {
        text: 'actions_machince_label_view',
        link: 'view',
        subItems: [],
        roles: authServices.getPermissionRoles('machines.view'),
    },
    {
        text: 'actions_machince_label_update',
        link: 'update',
        subItems: [],
        roles: authServices.getPermissionRoles('machines.update'),
    },
    {
        text: 'actions_machince_label_compatible_implements',
        link: 'compatible',
        subItems: [],
        roles: authServices.getPermissionRoles('compatibility.read'),
    },
    {
        text: 'actions_machince_label_delete',
        link: 'delete',
        subItems: [],
        roles: authServices.getPermissionRoles('machines.delete'),
    },
];

export const APP_IMPLEMENT_ACTIONS: INavigationItem[] = [
    {
        text: 'actions_implement_label_view',
        link: 'view',
        subItems: [],
        roles: authServices.getPermissionRoles('implements.view'),
    },
    {
        text: 'actions_implement_label_update',
        link: 'update',
        subItems: [],
        roles: authServices.getPermissionRoles('implements.update'),
    },
    {
        text: 'actions_implement_label_compatible_implements',
        link: 'compatible',
        subItems: [],
        roles: authServices.getPermissionRoles('compatibility.read'),
    },
    {
        text: 'actions_implement_label_delete',
        link: 'delete',
        subItems: [],
        roles: authServices.getPermissionRoles('implements.delete'),
    },
];

export const APP_DEFAULT_ADMIN_USER: AccountInfo = {
    environment: '1',
    homeAccountId: '',
    localAccountId: '',
    tenantId: '',
    username: import.meta.env.VITE_MSAL_ADMIN_ROLE_NAME,
    idTokenClaims: {
        roles: [import.meta.env.VITE_MSAL_ADMIN_ROLE_NAME],
        given_name: 'Mark Jons',
    },
};

export const APP_DEFAULT_READER_USER: AccountInfo = {
    environment: '1',
    homeAccountId: '',
    localAccountId: '',
    tenantId: '',
    username: import.meta.env.VITE_MSAL_READER_ROLE_NAME,
    idTokenClaims: {
        roles: [import.meta.env.VITE_MSAL_READER_ROLE_NAME],
        given_name: 'Mark Jons',
    },
};

export const APP_DEFAULT_USER_ROLE: string[] = [import.meta.env.VITE_MSAL_GUEST_ROLE_NAME];
